import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  
  // Prevent multiple simultaneous cleanup operations
  const isCleaningUp = useRef(false)
  // Track if we've already handled an invalid token to prevent loops
  const hasHandledInvalidToken = useRef(false)

  // Helper function to safely clear all auth data
  const clearAuthData = async (shouldSignOut = false) => {
    if (isCleaningUp?.current) return;
    
    isCleaningUp.current = true;
    try {
      // Clear all Supabase auth storage keys
      const authStorageKeys = [
        'supabase-auth-token',
        'sb-refresh-token',
        'sb-access-token'
      ];
      
      authStorageKeys?.forEach(key => {
        try {
          localStorage?.removeItem(key);
          sessionStorage?.removeItem(key);
        } catch (e) {
          console.error('Error clearing storage:', e);
        }
      });

      // Clear all supabase.auth storage items
      Object.keys(localStorage || {})?.forEach(key => {
        if (key?.startsWith('sb-') || key?.includes('supabase')) {
          try {
            localStorage?.removeItem(key);
          } catch (e) {
            console.error('Error clearing supabase storage:', e);
          }
        }
      });

      if (shouldSignOut) {
        try {
          await supabase?.auth?.signOut({ scope: 'local' });
        } catch (signOutError) {
          console.error('Sign out error:', signOutError);
        }
      }

      setUser(null);
      profileOperations?.clear();
    } finally {
      isCleaningUp.current = false;
    }
  };

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {

      if (!userId) {
         return
      }

      setProfileLoading(true);
      
      try {
        const { data, error } = await supabase
          .from('psychologists')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (error) {
          console.error('Profile load error:', error)
          // Don't throw - just log and continue
        } else {
          
          setUserProfile(data);

          // Check if user needs onboarding
          if (data && !data?.has_completed_onboarding) {
            setNeedsOnboarding(true)
          } else {
            setNeedsOnboarding(false)
          }
        }
      } catch (error) {
        console.error('Profile load exception:', error)
        // Don't throw - just log and continue
      } finally {
        setProfileLoading(false);
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setNeedsOnboarding(false)
    }
  }

  useEffect(() => {
    // Validate stored tokens before initializing auth
    const validateAndInitialize = async () => {
      try {
        // Check if there are any stored auth tokens
        const hasStoredTokens = Object.keys(localStorage || {})?.some(key => 
          key?.startsWith('sb-') || key?.includes('supabase') || key === 'supabase-auth-token'
        );

        if (!hasStoredTokens) {
          console.log('No stored tokens found - starting fresh');
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
          return;
        }

        // Try to get the current session
        const { data: { session }, error } = await supabase?.auth?.getSession();
        
        if (error) {
          console.warn('Session validation error:', error?.message);
          
          // Check for refresh token errors - these indicate invalid stored tokens
          const isRefreshTokenError = 
            error?.message?.includes('refresh_token_not_found') || 
            error?.message?.includes('Invalid Refresh Token') ||
            error?.message?.includes('Refresh Token Not Found') ||
            error?.status === 400;

          if (isRefreshTokenError && !hasHandledInvalidToken?.current) {
            console.log('Invalid refresh token detected - clearing all auth data');
            hasHandledInvalidToken.current = true;
            await clearAuthData(false); // Don't call signOut - tokens already invalid
            setLoading(false);
            // Reset the flag after a delay
            setTimeout(() => {
              hasHandledInvalidToken.current = false;
            }, 2000);
            return;
          }
          
          // For other errors, clear state but don't clear storage
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
        } else if (!session) {
          console.log('No active session found');
          setUser(null);
          profileOperations?.clear();
          setLoading(false);
        } else {
          console.log('Valid session restored for:', session?.user?.email);
          setUser(session?.user);
          if (session?.user) {
            profileOperations?.load(session?.user?.id);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        
        // On unexpected errors, clear everything to prevent loops
        if (!hasHandledInvalidToken?.current) {
          hasHandledInvalidToken.current = true;
          await clearAuthData(false);
          setTimeout(() => {
            hasHandledInvalidToken.current = false;
          }, 2000);
        }
        
        setLoading(false);
        setUser(null);
        profileOperations?.clear();
      }
    };

    validateAndInitialize();

    // Auth state change listener with improved error handling
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        console.log('Auth event:', event, 'Session:', !!session);
        
        // Ignore events if we're already cleaning up to prevent loops
        if (isCleaningUp?.current) {
          console.log('Ignoring auth event - cleanup in progress');
          return;
        }
        
        // Handle token refresh failures
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.error('Token refresh failed - clearing invalid tokens');
          if (!hasHandledInvalidToken?.current) {
            hasHandledInvalidToken.current = true;
            await clearAuthData(false);
            setLoading(false);
            setTimeout(() => {
              hasHandledInvalidToken.current = false;
            }, 2000);
          }
          return;
        }
        
        // Handle signed out state
        if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          await clearAuthData(false); // Tokens already cleared by signOut
          setLoading(false);
          return;
        }
        
        // Handle successful sign in or session update
        if (session?.user) {
          console.log('Session active for user:', session?.user?.email);
          hasHandledInvalidToken.current = false; // Reset on successful auth
          setUser(session?.user);
          profileOperations?.load(session?.user?.id);
          setLoading(false);
        } else if (!session && event !== 'SIGNED_OUT' && event !== 'TOKEN_REFRESHED') {
          // Session lost unexpectedly (not from sign out or token refresh)
          console.warn('Session lost unexpectedly on event:', event);
          if (!hasHandledInvalidToken?.current) {
            hasHandledInvalidToken.current = true;
            await clearAuthData(true);
            setLoading(false);
            setTimeout(() => {
              hasHandledInvalidToken.current = false;
            }, 2000);
          }
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
      isCleaningUp.current = false;
      hasHandledInvalidToken.current = false;
    };
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      // Reset invalid token flag on new sign in attempt
      hasHandledInvalidToken.current = false;
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      // Reset invalid token flag on new sign up attempt
      hasHandledInvalidToken.current = false;
      // Get the app URL for email confirmation redirect
      const appUrl = import.meta.env?.VITE_APP_URL || window.location?.origin;
      const emailRedirectTo = `${appUrl}/email-confirmation`;

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: emailRedirectTo
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    if (isCleaningUp?.current) {
      return { error: null };
    }
    
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        await clearAuthData(false); // Tokens already cleared by signOut
      }
      return { error };
    } catch (error) {
      console.error('Sign out error:', error);
      // Force clear even if signOut fails
      await clearAuthData(false);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase
        ?.from('psychologists')
        ?.update(updates)
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single()
      
      if (!error) {
        setUserProfile(data)
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const completeOnboarding = async () => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase
        ?.from('psychologists')
        ?.update({ has_completed_onboarding: true })
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single()
      
      if (!error) {
        setUserProfile(data)
        setNeedsOnboarding(false)
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    needsOnboarding,
    signIn,
    signUp,
    signOut,
    updateProfile,
    completeOnboarding,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}